import React, { useCallback, useEffect, useState, memo } from "react"
import { Paper, makeStyles, Typography, TextField, Button } from "@material-ui/core"
import PropTypes from "prop-types"

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: theme.spacing(2, 3, 3),
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		marginTop: theme.spacing(3),
	},
}))

const initialInputError = {
	error: false,
	errorText: "",
}

const EnterMFA = memo(
	({ cognitoUser, loginEntries, authenticationFlowStep, verifyTotpToken, enqueueSnackbar, closeSnackbar }) => {
		const classes = useStyles()
		const [inputTouched, setInputTouched] = useState(false)
		const [totpCode, setTotpCode] = useState("")
		const [totpInputError, setTotpInputError] = useState({ ...initialInputError })

		// TOTP code validation
		useEffect(() => {
			const handleTotpInputErrorUpdate = () => {
				if (totpCode.length === 6 && !isNaN(Number(totpCode))) {
					setTotpInputError({ ...initialInputError })
				} else {
					setTotpInputError({
						error: true,
						errorText: "Number must be 6 digits in length",
					})
				}
			}
			// This is a fix for warning: "unstable_flushDiscreteUpdates: Cannot flush updates when React is already rendering"
			// return inside useEffect to update totpInputError during cleanUp lifecycle, After totpCode is update, componentDidMount=true, and componentDidUpdate=true
			return handleTotpInputErrorUpdate()
		}, [totpCode])

		const submit = useCallback(
			(e) => {
				e.preventDefault()

				verifyTotpToken(cognitoUser, totpCode, loginEntries.password, authenticationFlowStep)
					.then()
					.catch((err) => {
						enqueueSnackbar(err.message ?? "An unexpected error occurred.", {
							variant: "error",
							persist: false,
							action: (key) => (
								<Button
									onClick={() => {
										closeSnackbar(key)
									}}
								>
									Dismiss
								</Button>
							),
						})
					})
			},
			[
				authenticationFlowStep,
				enqueueSnackbar,
				closeSnackbar,
				cognitoUser,
				loginEntries.password,
				totpCode,
				verifyTotpToken,
			],
		)

		return (
			<Paper className={classes.paper}>
				<Typography component="h1" variant="h5">
					Enter Authentication Code
				</Typography>
				<form className={classes.form} onSubmit={submit}>
					<TextField
						label="Code"
						margin="normal"
						variant="outlined"
						value={totpCode}
						onChange={(e) => {
							setInputTouched((prevState) => {
								if (prevState === false) {
									return true
								} else {
									return prevState
								}
							})
							setTotpCode(e.target.value)
						}}
						error={inputTouched ? totpInputError.error : false}
						helperText={inputTouched ? totpInputError.errorText : null}
						fullWidth
						autoFocus
						required
					/>
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
						disabled={totpInputError.error || totpCode === ""}
					>
						Submit
					</Button>
				</form>
			</Paper>
		)
	},
)

EnterMFA.propTypes = {
	cognitoUser: PropTypes.object,
	loginEntries: PropTypes.object,
	verifyTotpToken: PropTypes.func,
	authenticationFlowStep: PropTypes.string,
	enqueueSnackbar: PropTypes.func,
	closeSnackbar: PropTypes.any,
}

EnterMFA.displayName = "EnterMFA"

export default EnterMFA
